<template>
  <div class="server-detail-panel">
    <div class="server-detail-panel__crumb"><a href="#" @click.prevent="$router.push('/servers')">Servers</a> &rarr; <strong>{{ server ? (server.display_name || server.hostname) : '…' }}</strong></div>

    <div v-if="loadError" class="server-detail-panel__error">{{ loadError }}</div>

    <div v-if="server" class="server-detail-panel__panel">
      <div class="server-detail-panel__header">
        <div class="server-detail-panel__ph-left">
          <div class="server-detail-panel__ph-icon">
            <svg viewBox="0 0 18 18" fill="none" stroke="currentColor" stroke-width="1.6"><rect x="3" y="3" width="12" height="4.5" rx="1"/><rect x="3" y="10.5" width="12" height="4.5" rx="1"/><circle cx="6" cy="5.25" r=".4" fill="currentColor"/><circle cx="6" cy="12.75" r=".4" fill="currentColor"/></svg>
          </div>
          <div>
            <div class="server-detail-panel__ph-title">
              <h3>{{ server.display_name || server.hostname }}</h3>
              <span class="server-detail-panel__type-badge">Server</span>
              <span class="server-detail-panel__health-pill" :class="healthDotClass(server)">{{ healthLabel(server.health) }}</span>
              <AgentStatusBadge :status="server.agent_status" />
            </div>
            <div class="server-detail-panel__ph-sub">
              <span v-for="ip in ips" :key="ip.address">{{ ip.address }} ({{ ip.label }}) &middot; </span>
              {{ taxonomyLabel('provider', server.tags.provider) || 'No provider set' }}
            </div>
            <div class="server-detail-panel__ph-taxon">
              <span v-if="server.tags.environment" class="server-detail-panel__pill server-detail-panel__pill--env">{{ taxonomyLabel('environment', server.tags.environment) }}</span>
              <span v-for="role in server.tags.server_role" :key="role" class="server-detail-panel__pill server-detail-panel__pill--role">{{ taxonomyLabel('server_role', role) }}</span>
            </div>
          </div>
        </div>
        <div class="server-detail-panel__ph-right">
          <div class="server-detail-panel__agent-ver">{{ server.luna_version ? `Agent v${server.luna_version}` : 'No agent version reported' }}</div>
          <div class="server-detail-panel__agent-seen">{{ server.last_heartbeat_at ? `last seen ${formatRelativeTime(server.last_heartbeat_at)}` : 'never checked in' }}</div>
          <!-- Plan 011 §4.2 — archive/revoke actions. Archiving revokes the
               agent certificate the same day; the confirm modal below names
               that consequence explicitly rather than only in a tooltip. -->
          <div class="server-detail-panel__actions">
            <button
              v-if="!server.archived_at"
              type="button"
              class="server-detail-panel__action-btn server-detail-panel__action-btn--danger"
              :disabled="actionBusy"
              @click="archiveServer"
            >Archive</button>
            <template v-if="server.luna_agent_id">
              <button
                type="button"
                class="server-detail-panel__action-btn"
                :disabled="actionBusy"
                @click="rotateCertNow"
              >Rotate certificate now</button>
              <button
                type="button"
                class="server-detail-panel__action-btn"
                :disabled="actionBusy"
                @click="revokeCert"
              >Revoke agent certificate</button>
              <button
                type="button"
                class="server-detail-panel__action-btn"
                :disabled="actionBusy"
                @click="unrevokeCert"
              >Un-revoke</button>
            </template>
          </div>
          <div v-if="actionError" class="server-detail-panel__action-error">{{ actionError }}</div>
        </div>
      </div>

      <div class="server-detail-panel__tabs">
        <div
          v-for="tab in tabs"
          :key="tab.key"
          class="server-detail-panel__tab"
          :class="{ 'server-detail-panel__tab--active': activeTab === tab.key }"
          @click="activeTab = tab.key"
        >{{ tab.label }}</div>
      </div>

      <div class="server-detail-panel__body">
        <ServerOverviewTab v-if="activeTab === 'overview'" :api-base="apiBase" :server-id="serverId" :server="server" />
        <ServerServicesTab v-else-if="activeTab === 'services'" :api-base="apiBase" :server-id="serverId" />
        <ServerScheduledTasksTab v-else-if="activeTab === 'scheduled'" :api-base="apiBase" :server-id="serverId" />
        <ServerSecurityTab v-else-if="activeTab === 'security'" :api-base="apiBase" :server-id="serverId" :os="server.os || null" />
        <ServerVhostsTab v-else-if="activeTab === 'vhosts'" :api-base="apiBase" :server-id="serverId" />
        <ServerInventoryTab v-else-if="activeTab === 'inventory'" :api-base="apiBase" :server-id="serverId" />
        <ServerLogsTab v-else-if="activeTab === 'logs'" :api-base="apiBase" :server-id="serverId" />
        <ServerCommandsTab v-else-if="activeTab === 'commands'" :api-base="apiBase" :server-id="serverId" />
        <ServerConfigTab v-else-if="activeTab === 'config'" :server="server" />
      </div>
    </div>

    <div v-if="confirmModal.open" class="server-detail-panel__confirm-backdrop" @click.self="closeConfirm">
      <div
        ref="confirmBox"
        class="server-detail-panel__confirm-modal"
        role="alertdialog"
        aria-modal="true"
        aria-labelledby="server-detail-confirm-title"
        tabindex="-1"
        @keydown.esc.stop.prevent="closeConfirm"
        @keydown.tab="trapConfirmFocus"
      >
        <h3 id="server-detail-confirm-title">{{ CONFIRM_COPY[confirmModal.action].title }}</h3>
        <p>{{ CONFIRM_COPY[confirmModal.action].body }}</p>
        <div class="server-detail-panel__confirm-actions">
          <button ref="confirmCancelBtn" type="button" class="server-detail-panel__action-btn" @click="closeConfirm">Cancel</button>
          <button
            ref="confirmActionBtn"
            type="button"
            class="server-detail-panel__action-btn server-detail-panel__action-btn--danger"
            @click="runConfirmedAction"
          >{{ CONFIRM_COPY[confirmModal.action].confirmLabel }}</button>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { computed, nextTick, onMounted, reactive, ref } from 'vue';
import useApi from '@apexl/icarus-core/src/services/api';
import commonProps from '@apexl/icarus-core/src/components/_properties/common';
import AgentStatusBadge from '@root/components/Servers/AgentStatusBadge.vue';
import ServerOverviewTab from '@root/components/Servers/ServerOverviewTab.vue';
import ServerServicesTab from '@root/components/Servers/ServerServicesTab.vue';
import ServerScheduledTasksTab from '@root/components/Servers/ServerScheduledTasksTab.vue';
import ServerSecurityTab from '@root/components/Servers/ServerSecurityTab.vue';
import ServerVhostsTab from '@root/components/Servers/ServerVhostsTab.vue';
import ServerInventoryTab from '@root/components/Servers/ServerInventoryTab.vue';
import ServerLogsTab from '@root/components/Servers/ServerLogsTab.vue';
import ServerCommandsTab from '@root/components/Servers/ServerCommandsTab.vue';
import ServerConfigTab from '@root/components/Servers/ServerConfigTab.vue';
import { healthDotClass, healthLabel, taxonomyLabel, formatRelativeTime, parseIps } from '@root/services/serverFormat';

// `server-detail.html` — mounted at `/servers/{id}` via
// `ServersDisplayController::show()`. Tabs (Overview/Services/Logs/Commands/
// Config) switch CLIENT-SIDE (local `activeTab` ref) — no separate route or
// PHP dashboard route per tab, matching `drilldown-panel.html`'s anatomy
// scaled to a full route instead of a floating panel.
const props = defineProps({
  ...commonProps,
  apiBase: {
    type: String,
    default: '/api/v1/servers',
  },
  serverId: {
    type: [Number, String],
    required: true,
  },
});

const api = useApi();
const server = ref(null);
const loadError = ref(null);
const activeTab = ref('overview');
const ips = computed(() => (server.value ? parseIps(server.value) : []));
const actionBusy = ref(false);
const actionError = ref(null);

// Plan 011 §4.2 — in-panel confirmation for the two destructive,
// no-undo header actions (Archive, Revoke agent certificate), replacing a
// bare `window.confirm()` (one click + Enter dismissed it with no
// intermediate focus). Mirrors the backdrop/box idiom already used
// elsewhere in this codebase for in-panel modals (see
// `ServerEnrolmentModal.vue` and `AlertsAdminBoard.vue`'s
// `__modal-backdrop`/`__modal`) rather than introducing a new component.
const confirmModal = reactive({ open: false, action: null });
const confirmCancelBtn = ref(null);
const confirmActionBtn = ref(null);
const confirmBox = ref(null);
// Element that had focus when the modal opened (the header button), so
// closing hands focus back to it instead of dropping it on <body>.
let confirmReturnFocus = null;

const CONFIRM_COPY = {
  archive: {
    title: 'Archive this server?',
    body: 'Archiving this server will also revoke its agent certificate immediately — it will not be able to reconnect until re-adopted. Continue?',
    confirmLabel: 'Archive',
  },
  revoke: {
    title: 'Revoke agent certificate?',
    body: "This server's agent will be disconnected and cannot reconnect until you re-adopt the box. Continue?",
    confirmLabel: 'Revoke certificate',
  },
};

function openConfirm(action) {
  confirmReturnFocus = document.activeElement;
  confirmModal.action = action;
  confirmModal.open = true;
  // Default focus to Cancel, not the destructive action — same intent the
  // native confirm()'s "Cancel focused by default" had, made explicit here
  // since this modal has no browser-native default to fall back on.
  nextTick(() => confirmCancelBtn.value?.focus());
}

function closeConfirm() {
  confirmModal.open = false;
  confirmModal.action = null;
  const returnTo = confirmReturnFocus;
  confirmReturnFocus = null;
  if (returnTo?.isConnected) nextTick(() => returnTo.focus());
}

// Keyboard contract (a native confirm() gave these for free): Escape
// cancels (handled on the box), and Tab / Shift+Tab cycle between Cancel
// and the confirm button only — focus never walks out to the page behind
// the backdrop. The box itself is tabindex=-1 so a click on its text keeps
// focus (and these key handlers) inside the dialog.
function trapConfirmFocus(event) {
  const first = confirmCancelBtn.value;
  const last = confirmActionBtn.value;
  if (!first || !last) return;
  const active = document.activeElement;
  if (event.shiftKey && (active === first || active === confirmBox.value)) {
    event.preventDefault();
    last.focus();
  } else if (!event.shiftKey && (active === last || active === confirmBox.value)) {
    event.preventDefault();
    first.focus();
  }
}

async function runConfirmedAction() {
  const action = confirmModal.action;
  closeConfirm();
  if (action === 'archive') {
    await doArchive();
  } else if (action === 'revoke') {
    await doRevoke();
  }
}

const tabs = [
  { key: 'overview', label: 'Overview' },
  { key: 'services', label: 'Services' },
  { key: 'scheduled', label: 'Scheduled tasks' },
  { key: 'security', label: 'Security' },
  { key: 'vhosts', label: 'Domains & vhosts' },
  // Plan 010 Phase 1 — beside Domains & vhosts because they answer adjacent
  // questions about the same sites (what is served here / what is installed
  // there), and both are keyed on the same vhost discovery.
  { key: 'inventory', label: 'Inventory' },
  { key: 'logs', label: 'Logs' },
  { key: 'commands', label: 'Commands' },
  { key: 'config', label: 'Config' },
];

async function loadServer() {
  try {
    const data = await api.get(`${props.apiBase}/${props.serverId}`);
    server.value = data.server;
  } catch (err) {
    loadError.value = 'Could not load this server.';
  }
}

onMounted(loadServer);

// Plan 011 §4.2 — archiving revokes the agent cert the same day rather than
// leaving it valid for up to 180 days; NOT reversible without re-adopting
// the box (un-archiving isn't a button here — the normal recovery path is
// `luna adopt`, which mints a fresh certificate through the ordinary
// enrolment flow).
function archiveServer() {
  openConfirm('archive');
}

async function doArchive() {
  actionBusy.value = true;
  actionError.value = null;
  try {
    const data = await api.post(`${props.apiBase}/${props.serverId}/archive`, {});
    server.value = data.server;
    if (data.cert_revocation_error) {
      actionError.value = `Server archived, but the hub could not be told to revoke its certificate: ${data.cert_revocation_error}`;
    }
  } catch (err) {
    actionError.value = err?.response?.data?.error || 'Could not archive this server.';
  } finally {
    actionBusy.value = false;
  }
}

async function rotateCertNow() {
  // Plan 011 §3.1 — how the Phase 1 rollout is proven box-by-box. The hub
  // enforces connectivity (404) and a per-agent cooldown (429); both
  // surface here as a plain error rather than a silent no-op. The 429 body
  // is informational (a cooldown, not a real failure) rather than a reason
  // to leave stale data on screen, so it also refreshes.
  actionBusy.value = true;
  actionError.value = null;
  try {
    await api.post(`${props.apiBase}/${props.serverId}/rotate-cert`, {});
    await loadServer();
  } catch (err) {
    actionError.value = err?.response?.data?.error || 'Could not rotate this server\'s certificate right now.';
    if (err?.response?.status === 429) {
      await loadServer();
    }
  } finally {
    actionBusy.value = false;
  }
}

function revokeCert() {
  openConfirm('revoke');
}

async function doRevoke() {
  actionBusy.value = true;
  actionError.value = null;
  try {
    await api.post(`${props.apiBase}/${props.serverId}/revoke-cert`, {});
    await loadServer();
  } catch (err) {
    actionError.value = err?.response?.data?.error || 'Could not revoke this server\'s certificate.';
  } finally {
    actionBusy.value = false;
  }
}

async function unrevokeCert() {
  actionBusy.value = true;
  actionError.value = null;
  try {
    await api.post(`${props.apiBase}/${props.serverId}/unrevoke-cert`, {});
    await loadServer();
  } catch (err) {
    actionError.value = err?.response?.data?.error || 'Could not un-revoke this server\'s certificate.';
  } finally {
    actionBusy.value = false;
  }
}
</script>

<style lang="scss">
.server-detail-panel {
  &__crumb {
    font-size: 11.5px;
    color: var(--theme-text-tertiary);
    margin-bottom: 10px;

    a {
      color: var(--theme-text-tertiary);
    }

    strong {
      color: var(--theme-text-secondary);
      font-weight: 600;
    }
  }

  &__error {
    background: #f8dede;
    color: #a53232;
    border-radius: var(--theme-radius, 6px);
    padding: 10px 14px;
    font-size: 12.5px;
  }

  &__panel {
    background: var(--theme-bg-card);
    border: 1px solid var(--theme-border);
    border-radius: var(--theme-radius-lg, 10px);
    box-shadow: var(--theme-shadow-panel);
    overflow: hidden;
  }

  &__header {
    padding: 20px 24px;
    border-bottom: 1px solid var(--theme-border-soft);
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 16px;
    flex-wrap: wrap;
  }

  &__ph-left {
    display: flex;
    gap: 14px;
    align-items: center;
  }

  &__ph-icon {
    width: 44px;
    height: 44px;
    border-radius: var(--theme-radius, 6px);
    background: var(--theme-accent-light);
    color: var(--theme-accent-dark);
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;

    svg { width: 22px; height: 22px; }
  }

  &__ph-title {
    display: flex;
    align-items: center;
    gap: 9px;
    flex-wrap: wrap;

    h3 {
      margin: 0;
      font-size: 18px;
      font-weight: 600;
    }
  }

  &__type-badge {
    font-size: 10.5px;
    font-weight: 700;
    letter-spacing: .04em;
    text-transform: uppercase;
    background: var(--theme-border-soft);
    color: var(--theme-text-secondary);
    padding: 2px 7px;
    border-radius: var(--theme-radius-sm, 3px);
  }

  &__health-pill {
    display: inline-flex;
    align-items: center;
    gap: 5px;
    font-size: 11px;
    font-weight: 600;
    padding: 3px 9px;
    border-radius: 12px;
    background: var(--theme-border-soft);
    color: var(--theme-text-secondary);

    &.h-ok { background: #e1f3ea; color: #1c7a4d; }
    &.h-warn { background: #fbeeda; color: #8a5a0d; }
    &.h-crit { background: #f8dede; color: #a53232; }
    &.h-pending { background: #ddeaf4; color: #215d84; }
  }

  &__ph-sub {
    font-family: var(--theme-font-mono);
    font-size: 11.5px;
    color: var(--theme-text-tertiary);
    margin-top: 5px;
  }

  &__ph-taxon {
    display: flex;
    gap: 6px;
    margin-top: 8px;
    flex-wrap: wrap;
  }

  &__pill {
    display: inline-block;
    font-size: 10px;
    font-weight: 700;
    letter-spacing: .02em;
    padding: 2.5px 8px;
    border-radius: 3px;
    white-space: nowrap;

    &--env { background: var(--theme-border-soft); color: var(--theme-text-secondary); }
    &--role { background: var(--theme-accent-light); color: var(--theme-accent-dark); }
  }

  &__ph-right {
    text-align: right;
    flex-shrink: 0;
  }

  &__agent-ver {
    font-size: 12px;
    font-weight: 600;
    color: var(--theme-text-primary);
  }

  &__agent-seen {
    font-size: 11px;
    color: var(--theme-text-tertiary);
    margin-top: 2px;
  }

  &__actions {
    display: flex;
    gap: 8px;
    margin-top: 10px;
    justify-content: flex-end;
    flex-wrap: wrap;
  }

  &__action-btn {
    font-size: 11.5px;
    font-weight: 600;
    padding: 5px 10px;
    border-radius: var(--theme-radius-sm, 3px);
    border: 1px solid var(--theme-border-strong);
    background: var(--theme-bg-card);
    color: var(--theme-text-primary);
    cursor: pointer;

    &--danger {
      color: var(--theme-status-danger, #a53232);
      border-color: var(--theme-status-danger, #a53232);
    }

    &:disabled {
      opacity: .6;
      cursor: default;
    }
  }

  &__action-error {
    margin-top: 6px;
    font-size: 11px;
    color: var(--theme-status-danger, #a53232);
    max-width: 260px;
    text-align: right;
  }

  &__tabs {
    display: flex;
    gap: 2px;
    padding: 0 24px;
    border-bottom: 1px solid var(--theme-border);
    background: var(--theme-bg-card);
  }

  &__tab {
    padding: 12px 4px;
    margin-right: 26px;
    font-size: 13px;
    font-weight: 600;
    color: var(--theme-text-tertiary);
    border-bottom: 2px solid transparent;
    cursor: pointer;

    &--active {
      color: var(--theme-accent-dark);
      border-bottom-color: var(--theme-accent-primary);
    }
  }

  &__body {
    padding: 20px 24px 24px;
    background: var(--theme-bg-content);
  }

  &__confirm-backdrop {
    position: fixed;
    inset: 0;
    background: var(--theme-bg-overlay, rgba(14, 46, 48, 0.65));
    display: flex;
    align-items: flex-start;
    justify-content: center;
    padding: 12vh 16px;
    z-index: 1000;
  }

  &__confirm-modal {
    background: var(--theme-bg-card);
    border-radius: var(--theme-radius-lg, 10px);
    box-shadow: var(--theme-shadow-panel);
    width: min(420px, 92vw);
    padding: 20px 22px;

    &:focus {
      outline: none;
    }

    h3 {
      margin: 0 0 10px;
      font-size: 15px;
      font-weight: 600;
    }

    p {
      margin: 0;
      font-size: 12.5px;
      line-height: 1.55;
      color: var(--theme-text-secondary);
    }
  }

  &__confirm-actions {
    display: flex;
    justify-content: flex-end;
    gap: 8px;
    margin-top: 18px;
  }
}
</style>
